// Plays the stored speech for ONE completed turn, fetched from the backend as
// audio/mpeg (POST /api/companion/voice/speech {request_id}). This is the only
// voice the user hears. Rules: one playback per turn (explicit replay only),
// a newer play, user speech, close or clear stops the current one, every
// pending play() promise settles, Blob URLs are always revoked, and a failure
// offers a retry of the SAME result.
//
// Dependencies are injectable so the class is unit-tested under node.

const BASE = '/api/companion';

export class SpeechPlayer extends EventTarget {
  constructor({ fetchFn, AudioCtor, createObjectURL, revokeObjectURL } = {}) {
    super();
    this.fetchFn = fetchFn || ((...a) => globalThis.fetch(...a));
    this.AudioCtor = AudioCtor || globalThis.Audio;
    this.createObjectURL = createObjectURL || ((b) => URL.createObjectURL(b));
    this.revokeObjectURL = revokeObjectURL || ((u) => URL.revokeObjectURL(u));
    this.generation = 0;
    this.audio = null; this.url = null; this.controller = null;
    this.playedTurns = new Set();
    this.current = null;     // request_id of the active/blocked playback
    this.state = 'idle';
    this.settle = null;      // resolver for the active play() promise
  }

  setState(state, detail = {}) {
    this.state = state;
    this.dispatchEvent(new CustomEvent('state', { detail: { state, request_id: this.current, ...detail } }));
  }

  isPlaying() { return this.state === 'playing' || this.state === 'loading'; }

  // Returns 'played' | 'skipped' | 'stopped' | 'blocked' | 'error'.
  async play(request_id, { force = false } = {}) {
    if (!request_id) return 'error';
    if (!force && this.playedTurns.has(request_id)) return 'skipped';
    if (this.current === request_id && this.isPlaying()) return 'skipped'; // already going
    this.stop('replaced');
    const gen = ++this.generation;
    this.current = request_id;
    this.controller = new AbortController();
    const done = new Promise((resolve) => { this.settle = resolve; });
    this.setState('loading');
    try {
      const res = await this.fetchFn(`${BASE}/voice/speech`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ request_id }), signal: this.controller.signal
      });
      if (gen !== this.generation) return 'stopped';
      if (!res.ok) {
        let msg = `Speech unavailable (${res.status})`;
        try { const j = await res.json(); if (j?.error) msg = j.error; } catch { /* keep */ }
        this.finish(gen, 'error', { message: msg, status: res.status });
        return 'error';
      }
      const blob = await res.blob();
      if (gen !== this.generation) return 'stopped';
      this.url = this.createObjectURL(blob);
      const audio = new this.AudioCtor(this.url);
      try { audio.setAttribute?.('playsinline', ''); } catch { /* not an element in tests */ }
      this.audio = audio;
      this.playedTurns.add(request_id);
      audio.onended = () => this.finish(gen, 'idle');
      audio.onerror = () => this.finish(gen, 'error', { message: 'Playback failed.' });
      try {
        await audio.play();
        if (gen !== this.generation) return 'stopped';
        this.setState('playing');
      } catch (err) {
        if (gen !== this.generation) return 'stopped';
        // Autoplay policy: keep the blob; resume() after a tap finishes this
        // same play() promise through onended.
        this.setState('blocked', { message: err?.message || 'Tap to play.' });
      }
      return await done; // 'played' | 'stopped' | 'error'
    } catch (err) {
      if (err?.name === 'AbortError') { if (gen === this.generation) this.finish(gen, 'idle', { reason: 'aborted' }, 'stopped'); return 'stopped'; }
      if (gen === this.generation) this.finish(gen, 'error', { message: err?.message || 'Speech unavailable.' });
      return 'error';
    }
  }

  async resume() {
    if (!this.audio || this.state !== 'blocked') return false;
    try { await this.audio.play(); this.setState('playing'); return true; } catch { return false; }
  }

  // Shared completion: cleans media and settles the pending play() promise.
  finish(gen, state, detail = {}, result) {
    if (gen !== this.generation) return;
    this.cleanupMedia();
    const settle = this.settle; this.settle = null;
    const id = this.current;
    if (state === 'idle') this.current = null;
    this.setState(state, { ...detail, request_id: id });
    settle?.(result || (state === 'error' ? 'error' : 'played'));
  }

  stop(reason = 'stopped') {
    const gen = this.generation;
    this.generation++;
    try { this.controller?.abort(); } catch { /* ignore */ }
    this.controller = null;
    const active = !!this.audio || this.state === 'loading' || this.state === 'blocked' || this.state === 'playing';
    this.cleanupMedia();
    const settle = this.settle; this.settle = null;
    this.current = null;
    if (active) this.setState('idle', { reason, generation: gen });
    settle?.('stopped');
  }

  cleanupMedia() {
    if (this.audio) { try { this.audio.onended = null; this.audio.onerror = null; this.audio.pause?.(); this.audio.src = ''; } catch { /* ignore */ } }
    if (this.url) { try { this.revokeObjectURL(this.url); } catch { /* ignore */ } }
    this.audio = null; this.url = null;
  }

  dispose() { this.stop('disposed'); this.playedTurns.clear(); }
}
